import { Size } from '../types/props';

export type Dimensions = {
  width: number;
  height: number;
};

type UseGridItemDimensionsParams = {
  count: number;
  dimensions: Dimensions;
  aspectRatio: string;
  gap: number;
};

export type GridLayout = 'row' | 'column';

export interface GridSize {
  spotlight?: Exclude<Size, 'xl'>;
  mixed?: Exclude<Size, 'xl'>;
}

export type GridItemData = Dimensions & { rows: number; cols: number };

function roundDown(num: number, places: number): number {
  const factor = Math.pow(10, places);
  return Math.floor(num * factor) / factor;
}

export function useGridItemDimensions({
  count,
  dimensions,
  aspectRatio,
  gap,
}: UseGridItemDimensionsParams): GridItemData {
  let { width: W, height: H } = dimensions;
  if (W === 0 || H === 0) return { width: 0, height: 0, rows: 1, cols: 1 };

  W -= gap * 2;
  H -= gap * 2;

  const s = gap,
    N = count;
  const r = getAspectRatio(aspectRatio);

  let w = 0,
    h = 0;
  let a = 1,
    b = 1;

  const widths = [];

  for (let n = 1; n <= N; n++) {
    widths.push((W - s * (n - 1)) / n);
    widths.push((H - s * (n - 1)) / (n * r));
  }

  // sort in descending order, largest first
  // eslint-disable-next-line no-shadow
  widths.sort((a, b) => b - a);

  for (const width of widths) {
    // We fix the precision to 4 decimal places to prevent
    // floating point overflow errors. Anyway, accuracy above
    // 4 decimal places does not really matter.
    w = roundDown(width, 4);
    h = roundDown(w * r, 4);

    a = Math.floor((W + s) / (w + s));
    b = Math.floor((H + s) / (h + s));

    if (a * b >= N) {
      // recalculate rows, as row calculated above can be inaccurate
      b = Math.ceil(N / a);
      break;
    }
  }

  return { width: w, height: h, rows: b, cols: a };
}

/**
 * Parses the Aspect Ratio value
 * @param ratio The aspect ratio in the format of `16:9` where `width:height`
 * @returns The parsed value of aspect ratio
 */
export const getAspectRatio = (ratio: string): number => {
  const [width, height] = ratio.split(':');
  return Number.parseInt(height, 10) / Number.parseInt(width, 10);
};

export const defaultGridSize: GridSize = {
  spotlight: 'sm',
  mixed: 'sm',
};
